import { useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { submitBankDetails } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { BankDetailsSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';
import { checkAccountNumber, checkIfsc, checkVpa } from '@/validation/formats';

/**
 * BANK_DETAILS task (OL-22 slice 2): account number + IFSC (entered twice, so a typo is caught
 * here rather than by a failed penny drop), or a UPI ID. Shape checks only; the account is
 * verified later in the flow, and the room never shows that result.
 */
export function BankDetailsTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const mode = (task.spec as BankDetailsSpec | undefined)?.mode ?? 'ACCOUNT_IFSC';
  const [account, setAccount] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [holder, setHolder] = useState('');
  const [vpa, setVpa] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (task.status !== 'PENDING' || done) {
    return <Alert type="success" showIcon message={t('bank.submitted')} />;
  }

  const acct = account.replace(/\s/g, '');
  const code = ifsc.trim().toUpperCase();
  const mismatch = confirm.length > 0 && confirm.replace(/\s/g, '') !== acct;
  const ready =
    mode === 'VPA'
      ? checkVpa(vpa.trim()) === null
      : checkAccountNumber(acct) === null && checkIfsc(code) === null && !mismatch && confirm !== '';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await submitBankDetails(
        task.taskId,
        mode === 'VPA'
          ? { vpa: vpa.trim() }
          : { accountNumber: acct, ifsc: code, ...(holder.trim() ? { accountHolderName: holder.trim() } : {}) },
      );
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      setError(normalizeError(err).kind === 'INVALID' ? t('bank.invalid') : t('error.inline'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dr-stack">
      {mode === 'VPA' ? (
        <label className="dr-field">
          <span>{text('vpaLabel', 'bank.vpa')}</span>
          <Input
            size="large"
            value={vpa}
            spellCheck={false}
            autoCapitalize="off"
            placeholder="name@bank"
            status={vpa && checkVpa(vpa.trim()) ? 'error' : undefined}
            onChange={(e) => setVpa(e.target.value)}
            data-testid="bank-vpa"
          />
        </label>
      ) : (
        <>
          <label className="dr-field">
            <span>{text('holderLabel', 'bank.accountHolder')}</span>
            <Input size="large" value={holder} autoComplete="name" onChange={(e) => setHolder(e.target.value)} />
          </label>
          <label className="dr-field">
            <span>{text('accountLabel', 'bank.accountNumber')}</span>
            <Input
              size="large"
              value={account}
              inputMode="numeric"
              autoComplete="off"
              status={account && checkAccountNumber(acct) ? 'error' : undefined}
              onChange={(e) => setAccount(e.target.value)}
              data-testid="bank-account"
            />
          </label>
          <label className="dr-field">
            <span>{text('confirmLabel', 'bank.confirmAccount')}</span>
            <Input
              size="large"
              value={confirm}
              inputMode="numeric"
              autoComplete="off"
              onPaste={(e) => e.preventDefault()}
              status={mismatch ? 'error' : undefined}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="bank-confirm"
            />
          </label>
          {mismatch && (
            <div className="dr-meta dr-meta--error" role="alert">
              {t('bank.mismatch')}
            </div>
          )}
          <label className="dr-field">
            <span>{text('ifscLabel', 'bank.ifsc')}</span>
            <Input
              size="large"
              value={ifsc}
              spellCheck={false}
              autoCapitalize="characters"
              placeholder="ABCD0123456"
              status={ifsc && checkIfsc(code) ? 'error' : undefined}
              onChange={(e) => setIfsc(e.target.value)}
              data-testid="bank-ifsc"
            />
          </label>
        </>
      )}
      {error && <Alert type="error" showIcon message={error} />}
      <Button type="primary" size="large" block disabled={!ready} loading={busy} icon={<SendOutlined />} onClick={submit}>
        {text('submit', 'bank.submit')}
      </Button>
    </div>
  );
}
